<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filterParams" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/tours/csv"
      :filter="providerParams.filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="main-area h-full">
    <div class="ml-12">
      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          :row-link="(reg) => reg?.driver ? ({ name: 'driver-details', params: { driverId: reg?.driver?.id } }) : undefined"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref, inject, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {
  useProvider,
  collOptionsProvider,
  type Filter as ProviderFilter,
  ProviderParams,
  enumOptionsProvider
} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import type { Tour, HydraCollection, Company, License, LicenseSeatRegistration } from '@/entities/Entities';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import Tabs from '@components/structure/navigation/Tabs.vue';
import {useTableFields} from '@hooks/table-configuration';
import globalLicenseFilterKey from '@/components/utils/globalLicenseFilter/injects';
import { useRoute } from 'vue-router';
import { fetchApi } from '@/hooks/fetch';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<ProviderFilter>('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

interface Props {
  modelValue: Company | null;
}

const props = defineProps<Props>();

const globalFilter = inject(globalLicenseFilterKey)!

const filterLicense = ref<License>();
watch(() => globalFilter.value.licenseId, async () => {
  if(!globalFilter.value.licenseId) {
    filterLicense.value = undefined;
    return;
  }

  filterLicense.value = await fetchApi<License>(globalFilter.value.licenseId);
}, {immediate: true});
const csvTitle = computed(() => {
  let title = t('csv.title.toursFromCompany', {
    company: props.modelValue?.name,
  });

  if(filterLicense.value) {
    title += `_${filterLicense.value?.startDate ? format(new Date(filterLicense.value.startDate), 'dd-MM-yyyy') : ''}_${filterLicense.value?.endDate ? format(new Date(filterLicense.value.endDate), 'dd-MM-yyyy') : ''}`;
  }

  if(globalFilter.value?.boxType) {
    title += `_${t('licenses.boxType.'+globalFilter.value.boxType)}`;
  }

  return title;
});

const route = useRoute();

const sortParams = reactive({
  sortKey,
  sortDirection,
})
const filterParams = ref<ProviderFilter>(filter.value)
const paginationParams = reactive({
  page,
  perPage,
})
const providerParams = computed<ProviderParams>(() => {
  const filter: ProviderFilter = {
    ...filterParams.value,
    'exists[licenseSeatRegistration]': true,
    'licenseSeatRegistration.licenseSeat.license.company': `/companies/${route.params.companyId}`,
  }

  if(globalFilter.value?.licenseId) {
    filter['licenseSeatRegistration.licenseSeat.license'] = globalFilter.value.licenseId
  }
  if(globalFilter.value?.boxType) {
    filter['licenseSeatRegistration.licenseSeat.type'] = globalFilter.value.boxType
  }

  return {
    sort: sortParams,
    filter,
    pagination: paginationParams,
  }
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.tours.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'createdAt',
    title: t('tour.createdAt'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('tour.createdAt'),
      },
    },
  },
  {
    key: 'uploadedAt',
    title: t('uploadedAt'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('uploadedAt'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.tours.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'driver.canton',
    title: t('licenses.canton'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`canton.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Canton', (v) => ({
          value: v,
          label: v ? t(`canton.${v}`) : '-',
        })),
        label: t('licenses.canton'),
      },
    },
  },
  {
    key: 'distance',
    title: t('tour.distance'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        icon: <span>km</span>,
        label: t('tour.distance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'time',
    title: t('tour.time'),
    group: t('pages.tours.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.tours.fieldGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('tour.time'),
      },
    },
  },
  {
    key: 'averageSpeed',
    title: t('tour.speed'),
    group: t('pages.tours.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => (v * 3.6).toFixed(2) + ' km/h',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.calculations'),
      options: {
        icon: <span>km/h</span>,
        label: t('tour.speed'),
        step: 'any',
        min: '0',
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'kcal',
    title: t('tour.kcal'),
    group: t('pages.tours.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.calculations'),
      options: {
        icon: <span>kcal</span>,
        label: t('tour.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('tour.co2'),
    group: t('pages.tours.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'kg',
    filter: {
      type: 'range',
      group: t('pages.tours.fieldGroups.calculations'),
      options: {
        icon: <span>t</span>,
        label: t('tour.co2'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license',
    title: t('driver.license'),
    sortable: false,
    visible: true,
    group: t('pages.driverList.fieldGroups.license'),
    computed: (tour: Tour) =>
      tour.licenseSeatRegistration?.licenseSeat?.license ?
        `${tour.licenseSeatRegistration?.licenseSeat?.license?.company?.name} - ${tour.licenseSeatRegistration?.licenseSeat?.license?.startDate ? format(new Date(tour.licenseSeatRegistration?.licenseSeat?.license?.startDate), 'dd.MM.yyyy') : ''} - ${tour.licenseSeatRegistration?.licenseSeat?.license?.endDate ? format(new Date(tour.licenseSeatRegistration?.licenseSeat?.license?.endDate), 'dd.MM.yyyy') : ''}`
        : ''
    ,
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.type',
    title: t('pages.driverList.fields.licenseType'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`licenses.boxType.${v}`),
    csvTranslations: {
      box: t('licenses.boxType.box'),
      noBox: t('licenses.boxType.noBox'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.fieldGroups.license'),
      options: {
        options: enumOptionsProvider('/enums/LicenseType', (v) => ({
          value: v,
          label: t(`licenses.boxType.${v}`),
        })),
        label: t('pages.driverList.fields.licenseType'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.startDate',
    title: t('licenses.startDate'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('licenses.startDate'),
      },
    },
  },
  {
    key: 'licenseSeatRegistration.licenseSeat.license.endDate',
    title: t('licenses.endDate'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('licenses.endDate'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const { data: items, loading } = useProvider<HydraCollection<Tour>>('/tours', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
